/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { TabbedFullWidthAssetItemFragment } from "@cosmos/hygraph-schema";
import {
	CosmosTextVariantEnum,
	CosmosButtonProps,
	CosmosText,
	ButtonList,
	CosmosDesc,
	ColorfulTitle,
} from "@components/elements";
import { css, cx } from "@panda/css";
import { VStack } from "@panda/jsx";

const styles = css({
	h: "full",
	maxWidth: "686px",
});

const headingStyles = css({ textTransform: "lowercase", "&:first-letter": { textTransform: "uppercase" } });
const titleStyles = css({
	mdDown: {
		_landscape: {
			textStyle: "headingXL",
		},
	},
	smDown: {
		textStyle: "headingXL",
	},
});
const descStyles = css({
	mdDown: {
		_landscape: {
			textStyle: "paragraphSM",
		},
	},
	smDown: {
		textStyle: "paragraphMD",
	},
});
const buttonsStyles = css({
	marginBlockStart: "eds.dimension.32",
	mdDown: {
		_landscape: {
			marginBlockStart: "eds.dimension.10",
		},
	},
	smDown: {
		flexWrap: "wrap",
		alignItems: "flex-start",
	},
});

export const TabbedFullWidthAssetMainStack = ({
	eyebrow,
	buttons = [],
	title,
	desc,
	gradient,
}: TabbedFullWidthAssetItemFragment): React.JSX.Element | null => {
	if (!title?.text && !desc && !buttons.length) {
		return null;
	}

	const cosmosButtons = buttons.map((button): CosmosButtonProps => {
		return {
			...button,
			size: "lg",
			analytics: {
				eventCategory: "button",
				interactionType: "click",
				eventAction: "navigation",
				eventLabel: button.name || button.label || "",
				eventValue: {
					component: "TabbedFullWidthAsset",
					section: "hero",
				},
			},
		};
	});

	return (
		<VStack
			justifyContent="center"
			height="100%"
			alignItems="flex-start"
			className={cx(`tabbed-full-width-asset-main-stack`, styles)}
		>
			<VStack gap="eds.dimension.24" alignItems="flex-start">
				<CosmosText variant="headingSM" className={headingStyles}>
					{eyebrow}
				</CosmosText>

				<VStack alignItems="flex-start" gap="eds.dimension.24">
					<ColorfulTitle
						gradient={gradient}
						title={{ ...title, variant: "heading2XL", color: "eds.text.contrast" }}
						className={titleStyles}
					/>

					<CosmosDesc
						description={{
							html: desc,
							variant: CosmosTextVariantEnum.paragraphMD,
							as: "div",
							color: "eds.text.contrast",
						}}
						className={descStyles}
					/>
				</VStack>
			</VStack>
			<div className={cx("buttons-section", buttonsStyles)}>
				<ButtonList buttons={cosmosButtons} />
			</div>
		</VStack>
	);
};
